import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { MatterMemberRead } from '@/matter/components/manage-members/schemas'

interface MatterMembersEntry {
  members: MatterMemberRead[]
  loading: boolean
  error: boolean
  /* Set once the first fetch succeeds; a later failed or in-flight poll never resets it, so the loaded list stays visible. */
  initialLoadComplete: boolean
  /* Monotonic id of the latest request; a response tagged with an older id is dropped so a slow fetch can't overwrite fresher data. */
  requestId: number
  /* Timestamp of the last successful fetch, used by the listener's cache guard. */
  lastFetch: number | null
}

export interface CollaborationState {
  membersByMatterId: Record<string, MatterMembersEntry>
}

const initialState: CollaborationState = {
  membersByMatterId: {},
}

export const collaborationSlice = createSlice({
  name: 'collaborationState',
  initialState,
  reducers: {
    // ============== Listener Middleware Triggers ============== >
    fetchMembers: (_state, _action: PayloadAction<{ matterId: string; force?: boolean }>) => {
      // Handled by listener middleware
    },

    // ============== Slice Actions ============== >
    nullifyData: () => initialState,

    fetchMembersStarted: (state, action: PayloadAction<{ matterId: string; requestId: number }>) => {
      const { matterId, requestId } = action.payload
      const existing = state.membersByMatterId[matterId]
      state.membersByMatterId[matterId] = {
        members: existing?.members ?? [],
        loading: true,
        error: false,
        initialLoadComplete: existing?.initialLoadComplete ?? false,
        requestId,
        lastFetch: existing?.lastFetch ?? null,
      }
    },
    fetchMembersSucceeded: (state, action: PayloadAction<{ matterId: string; members: MatterMemberRead[]; requestId: number }>) => {
      const { matterId, members, requestId } = action.payload
      const existing = state.membersByMatterId[matterId]
      if (existing && requestId < existing.requestId) return
      state.membersByMatterId[matterId] = {
        members,
        loading: false,
        error: false,
        initialLoadComplete: true,
        requestId,
        lastFetch: Date.now(),
      }
    },
    fetchMembersFailed: (state, action: PayloadAction<{ matterId: string; requestId: number }>) => {
      const { matterId, requestId } = action.payload
      const entry = state.membersByMatterId[matterId]
      if (!entry || requestId < entry.requestId) return
      entry.loading = false
      entry.error = true
    },
  },
})

export const CollaborationActions = collaborationSlice.actions
export default collaborationSlice.reducer
